import { diffArrays } from "diff";

/** Ίδιο με Python `str.split()` (χωρίς args): κενά → [], αλλιώς trim + split σε λέξεις */
export function tokenizeLikePython(text: string): string[] {
    if (!text || !/\S/.test(text)) return [];
    return text.trim().split(/\s+/);
}

/** Λέξεις όπως το `tokenizeLikePython`, με θέσεις στο αρχικό string (για highlight στο UI) */
export function splitWordsWithSpans(text: string): {
    word: string;
    start: number;
    end: number;
}[] {
    const result: { word: string; start: number; end: number }[] = [];
    let i = 0;
    while (i < text.length) {
        while (i < text.length && /\s/.test(text[i]!)) i++;
        if (i >= text.length) break;
        const start = i;
        while (i < text.length && !/\s/.test(text[i]!)) i++;
        result.push({ word: text.slice(start, i), start, end: i });
    }
    return result;
}

export type WordDiffRow = {
    αριθμός: number;
    τύπος: "ίδιο" | "αφαίρεση" | "προσθήκη" | "αντικατάσταση";
    "λέξεις (πρωτότυπο)": string;
    "λέξεις (debiased)": string;
    "απόσπασμα": string;
};

function rangeLabel(start: number, end: number): string {
    if (end <= start) return "—";
    if (end - start === 1) return `${start + 1}`;
    return `${start + 1}–${end}`;
}

/**
 * Diff σε επίπεδο λέξεων (ίδια λογική με sequenceMatcher.py: tokens + ευθυγράμμιση).
 * Επιστρέφει γραμμές για πίνακα και λεπτομέρειες για styled console.
 */
export function buildWordDiffRows(original: string, debiased: string): {
    rows: WordDiffRow[];
    segments: ReturnType<typeof buildSegments>;
} {
    const origTokens = tokenizeLikePython(original);
    const debTokens = tokenizeLikePython(debiased);
    const segments = buildSegments(origTokens, debTokens);
    const rows: WordDiffRow[] = [];
    let n = 0;

    for (const seg of segments) {
        if (seg.kind === "equal") continue;

        n += 1;
        if (seg.kind === "remove") {
            rows.push({
                αριθμός: n,
                τύπος: "αφαίρεση",
                "λέξεις (πρωτότυπο)": rangeLabel(seg.origStart, seg.origEnd),
                "λέξεις (debiased)": "—",
                απόσπασμα: `− ${seg.text}`,
            });
        } else if (seg.kind === "add") {
            rows.push({
                αριθμός: n,
                τύπος: "προσθήκη",
                "λέξεις (πρωτότυπο)": "—",
                "λέξεις (debiased)": rangeLabel(seg.debStart, seg.debEnd),
                απόσπασμα: `+ ${seg.text}`,
            });
        } else {
            rows.push({
                αριθμός: n,
                τύπος: "αντικατάσταση",
                "λέξεις (πρωτότυπο)": rangeLabel(seg.origStart, seg.origEnd),
                "λέξεις (debiased)": rangeLabel(seg.debStart, seg.debEnd),
                απόσπασμα: `− ${seg.removed}  →  + ${seg.added}`,
            });
        }
    }

    return { rows, segments };
}

type Segment =
    | {
          kind: "equal";
          text: string;
          origStart: number;
          origEnd: number;
          debStart: number;
          debEnd: number;
      }
    | { kind: "remove"; text: string; origStart: number; origEnd: number }
    | { kind: "add"; text: string; debStart: number; debEnd: number }
    | {
          kind: "replace";
          removed: string;
          added: string;
          origStart: number;
          origEnd: number;
          debStart: number;
          debEnd: number;
      };

function buildSegments(origTokens: string[], debTokens: string[]): Segment[] {
    const raw = diffArrays(origTokens, debTokens);
    const out: Segment[] = [];
    let o = 0;
    let d = 0;

    for (let i = 0; i < raw.length; i++) {
        const part = raw[i];
        const next = raw[i + 1];

        if (part.removed && next?.added) {
            const oStart = o;
            const oEnd = o + part.value.length;
            const dStart = d;
            const dEnd = d + next.value.length;
            out.push({
                kind: "replace",
                removed: part.value.join(" "),
                added: next.value.join(" "),
                origStart: oStart,
                origEnd: oEnd,
                debStart: dStart,
                debEnd: dEnd,
            });
            o = oEnd;
            d = dEnd;
            i++;
            continue;
        }

        if (part.removed) {
            const oStart = o;
            const oEnd = o + part.value.length;
            out.push({
                kind: "remove",
                text: part.value.join(" "),
                origStart: oStart,
                origEnd: oEnd,
            });
            o = oEnd;
            continue;
        }

        if (part.added) {
            const dStart = d;
            const dEnd = d + part.value.length;
            out.push({
                kind: "add",
                text: part.value.join(" "),
                debStart: dStart,
                debEnd: dEnd,
            });
            d = dEnd;
            continue;
        }

        const len = part.value.length;
        const oStart = o;
        const dStart = d;
        o += len;
        d += len;
        out.push({
            kind: "equal",
            text: part.value.join(" "),
            origStart: oStart,
            origEnd: o,
            debStart: dStart,
            debEnd: d,
        });
    }

    return out;
}

export type DebiasedRenderPiece =
    | { kind: "plain"; text: string }
    | { kind: "mark"; text: string; change: "add" }
    | {
          kind: "mark";
          text: string;
          change: "replace";
          removed: string;
      };

function mergeAdjacentPlainPieces(pieces: DebiasedRenderPiece[]): DebiasedRenderPiece[] {
    const out: DebiasedRenderPiece[] = [];
    for (const p of pieces) {
        const last = out[out.length - 1];
        if (p.kind === "plain" && last?.kind === "plain") {
            last.text += p.text;
        } else {
            out.push(p);
        }
    }
    return out;
}

/**
 * Χωρίζει το debiased κείμενο σε κομμάτια για εμφάνιση: κοινό κείμενο + σημειωμένες προσθήκες/αντικαταστάσεις.
 * Διατηρεί κενά/αλλαγές γραμμής όπως στο αρχικό debiased string.
 */
export function buildDebiasedRenderPieces(
    original: string,
    debiased: string
): DebiasedRenderPiece[] {
    const origTokens = tokenizeLikePython(original);
    const debSpans = splitWordsWithSpans(debiased);
    const debTokens = debSpans.map((s) => s.word);

    if (debSpans.length === 0) {
        return [{ kind: "plain", text: debiased }];
    }

    const segments = buildSegments(origTokens, debTokens);
    const pieces: DebiasedRenderPiece[] = [];
    let lastCharEnd = 0;

    const charRange = (debStart: number, debEnd: number) => {
        if (debEnd <= debStart) return null;
        const a = debSpans[debStart];
        const b = debSpans[debEnd - 1];
        if (!a || !b) return null;
        return { start: a.start, end: b.end };
    };

    for (const seg of segments) {
        if (seg.kind === "remove") continue;

        const dr =
            seg.kind === "equal"
                ? charRange(seg.debStart, seg.debEnd)
                : seg.kind === "add"
                  ? charRange(seg.debStart, seg.debEnd)
                  : charRange(seg.debStart, seg.debEnd);

        if (!dr) continue;

        if (dr.start > lastCharEnd) {
            pieces.push({ kind: "plain", text: debiased.slice(lastCharEnd, dr.start) });
        }

        const slice = debiased.slice(dr.start, dr.end);

        if (seg.kind === "equal") {
            pieces.push({ kind: "plain", text: slice });
        } else if (seg.kind === "add") {
            pieces.push({ kind: "mark", text: slice, change: "add" });
        } else {
            pieces.push({
                kind: "mark",
                text: slice,
                change: "replace",
                removed: seg.removed,
            });
        }
        lastCharEnd = dr.end;
    }

    if (lastCharEnd < debiased.length) {
        pieces.push({ kind: "plain", text: debiased.slice(lastCharEnd) });
    }

    return mergeAdjacentPlainPieces(pieces);
}

/**
 * Όμορφη έξοδο στο DevTools: ομάδα, πίνακας αλλαγών, χρωματισμένες λεπτομέρειες ανά σημείο.
 */
export function logSequenceMatcherStyleDiff(
    original: string,
    debiased: string,
    // label = "Debias — διαφορές (επίπεδο λέξεων)"
): void {
    const { rows, 
        // segments
     } = buildWordDiffRows(original, debiased);

    // console.groupCollapsed(
    //     `%c${label}`,
    //     "font-weight:600;font-size:13px;color:#1d4ed8;"
    // );

    // const origTokens = tokenizeLikePython(original);
    // const debTokens = tokenizeLikePython(debiased);
    // console.log(
    //     "%cΣύνοψη:%c πρωτότυπο %c%d%c λέξεις · debiased %c%d%c λέξεις · %c%d%c αλλαγές (τμήματα)",
    //     "color:#64748b",
    //     "color:inherit",
    //     "font-weight:600;color:#0f172a",
    //     origTokens.length,
    //     "color:inherit",
    //     "font-weight:600;color:#0f172a",
    //     debTokens.length,
    //     "color:inherit",
    //     "font-weight:600;color:#0f172a",
    //     rows.length,
    //     "color:inherit"
    // );

    if (rows.length === 0) {
        // console.log("%cΔεν βρέθηκε διαφορά σε επίπεδο λέξεων.", "color:#16a34a;");
        // console.groupEnd();
        return;
    }

    // console.table(rows);

    // console.log(
    //     "%cΛεπτομέρειες ανά σημείο (1-based θέσεις λέξεων):",
    //     "font-weight:600;color:#334155;"
    // );

    // let step = 0;
    // for (const seg of segments) {
    //     if (seg.kind === "equal") continue;
    //     step += 1;

    //     if (seg.kind === "remove") {
    //         console.log(
    //             `%c#${step} Αφαίρεση%c · λέξεις πρωτότυπου ${rangeLabel(seg.origStart, seg.origEnd)}`,
    //             "color:#b91c1c;font-weight:600",
    //             "color:#334155"
    //         );
    //         console.log("%c− " + seg.text, "color:#991b1b;background:#fef2f2;padding:2px 6px;border-radius:4px;");
    //     } else if (seg.kind === "add") {
    //         console.log(
    //             `%c#${step} Προσθήκη%c · λέξεις debiased ${rangeLabel(seg.debStart, seg.debEnd)}`,
    //             "color:#15803d;font-weight:600",
    //             "color:#334155"
    //         );
    //         console.log("%c+ " + seg.text, "color:#166534;background:#f0fdf4;padding:2px 6px;border-radius:4px;");
    //     } else {
    //         console.log(
    //             `%c#${step} Αντικατάσταση%c · πρωτότυπο ${rangeLabel(seg.origStart, seg.origEnd)} → debiased ${rangeLabel(seg.debStart, seg.debEnd)}`,
    //             "color:#c2410c;font-weight:600",
    //             "color:#334155"
    //         );
    //         console.log(
    //             "%c− " + seg.removed,
    //             "color:#991b1b;background:#fef2f2;padding:2px 6px;border-radius:4px;"
    //         );
    //         console.log(
    //             "%c+ " + seg.added,
    //             "color:#166534;background:#f0fdf4;padding:2px 6px;border-radius:4px;"
    //         );
    //     }
    // }

    // console.groupEnd();
}